<template>
  <div 
    class="notification-history-panel"
    :class="{ 'notification-history-panel--visible': visible }"
    role="dialog"
    aria-labelledby="notification-history-title"
    :aria-hidden="!visible"
  >
    <!-- Header -->
    <div class="notification-history-panel__header">
      <h2 id="notification-history-title" class="notification-history-panel__title">
        <Icon name="bell" :size="16" />
        Notifications
      </h2>
      <div class="notification-history-panel__header-actions">
        <Button
          variant="icon"
          icon="empty"
          size="small"
          @click="handleClearAll"
          :title="'Clear all notifications'"
          :disabled="!notifications.length"
        />
        <!-- <Button
          variant="icon"
          icon="notification"
          size="small"
          @click="handleClose"
          :title="'Close notification panel'"
        /> -->
      </div>
    </div>

    <!-- Notification List -->
    <div class="notification-history-panel__content">
      <div v-if="notifications.length === 0" class="notification-history-panel__empty">
        <Icon name="bell-slash" :size="24" />
        <h3>No new notifications</h3>
      </div>

      <div v-else class="notification-history-panel__list">
        <NotificationHistoryItem
          v-for="notification in sortedNotifications"
          :key="notification.id"
          :notification="notification"
          @remove="handleRemoveFromHistory"
          @action="handleNotificationAction"
          @expand="handleExpand"
          @collapse="handleCollapse"
        />
      </div>
    </div>

    <!-- Footer Actions -->
    <div v-if="notifications.length > 0" class="notification-history-panel__footer">
      <div class="footer-info">
        {{ notifications.length }} notification{{ notifications.length === 1 ? '' : 's' }}
      </div>
      <div class="footer-actions">
        <Button
          variant="secondary"
          size="small"
          @click="handleClearAll"
        >
          Clear all
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../atoms/Icon.vue';
import Button from '../atoms/Button.vue';
import NotificationHistoryItem from '@/components/molecules/NotificationHistoryItem.vue';
import type { 
  ToastNotification
} from '@/types/notifications';

export interface NotificationHistoryPanelProps {
  visible?: boolean;
  notifications: ToastNotification[];
}

const props = withDefaults(defineProps<NotificationHistoryPanelProps>(), {
  visible: false,
});

const emit = defineEmits<{
  close: [];
  'clear-all': [];
  'remove-from-history': [id: string];
  'notification-action': [notificationId: string, actionId: string];
  expand: [id: string];
  collapse: [id: string];
}>();

// Computed properties
const sortedNotifications = computed(() => {
  // Sort by original creation date (newest first) - use originalCreatedAt to maintain consistent order
  return [...props.notifications].sort((a, b) => b.originalCreatedAt.getTime() - a.originalCreatedAt.getTime());
});

// Event handlers
// @ts-ignore - Used in template @click handler
const handleClose = () => {
  emit('close');
};

const handleClearAll = () => {
  emit('clear-all');
};

const handleRemoveFromHistory = (id: string) => {
  emit('remove-from-history', id);
};

const handleNotificationAction = (notificationId: string, actionId: string) => {
  emit('notification-action', notificationId, actionId);
};

const handleExpand = (id: string) => {
  emit('expand', id);
};

const handleCollapse = (id: string) => {
  emit('collapse', id);
};
</script>

<style scoped lang="scss">
.notification-history-panel {
  position: relative; // Changed from fixed to relative - let parent handle positioning
  // Removed top and right positioning - handled by parent wrapper
  width: 100%; // Use full width of parent container
  max-height: 80vh;
  background: var(--vscode-sideBar-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg, 0 8px 16px rgba(0, 0, 0, 0.3));
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transform: translateY(-10px) scale(0.95);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &--visible {
    transform: translateY(0) scale(1);
    opacity: 1;
    visibility: visible;
  }
  
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-md) var(--spacing-sm);
    border-bottom: 1px solid var(--vscode-widget-border);
    flex-shrink: 0;
  }
  
  &__title {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: var(--vscode-foreground);
  }
  
  &__header-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }
  
  &__stats {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--vscode-editor-background);
    border-bottom: 1px solid var(--vscode-widget-border);
    
    .stats-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: 12px;
      color: var(--vscode-descriptionForeground);
    }
  }
  
  &__filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--vscode-editor-background);
    border-bottom: 1px solid var(--vscode-widget-border);
    flex-shrink: 0;
    
    .filter-group {
      display: flex;
      gap: var(--spacing-sm);
      flex: 1;
    }
    
    .filter-actions {
      display: flex;
      gap: var(--spacing-xs);
    }
  }
  
  &__content {
    flex: 1;
    overflow-y: auto;
    min-height: min-content;
  }
  
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xxl);
    text-align: center;
    color: var(--vscode-descriptionForeground);
    
    h3 {
      margin: var(--spacing-md) 0 var(--spacing-sm) 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--vscode-foreground);
    }
    
    p {
      margin: 0;
      font-size: 13px;
      opacity: 0.8;
    }
  }
  
  &__list {
    padding: var(--spacing-xs) 0;
  }
  
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-sm) var(--spacing-md);
    border-top: 1px solid var(--vscode-widget-border);
    background: var(--vscode-editor-background);
    flex-shrink: 0;
    
    .footer-info {
      font-size: 12px;
      color: var(--vscode-descriptionForeground);
    }
    
    .footer-actions {
      display: flex;
      gap: var(--spacing-xs);
    }
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .notification-history-panel {
    // Remove positioning overrides - let parent handle positioning
    width: 100%; // Use full width of parent container
    max-height: calc(100vh - 80px);
    
    &__filters {
      flex-direction: column;
      align-items: stretch;
      gap: var(--spacing-sm);
      
      .filter-group {
        justify-content: stretch;
      }
      
      .filter-actions {
        justify-content: center;
      }
    }
  }
}
</style>
